import {ConfEnv} from "@/config/config";
import {isGamanzaUiEnabled} from "@/utils/gamanzaUi";

const EMPTY_STATS = {
  points: 0,
  tokens: 0,
  level: 0,
  rankName: null,
  xpBalance: 0,
  levelProgress: null,
  pointsUntilNextLevel: null,
};

const EMPTY_ACHIEVEMENTS = {
  missions: [],
  bundles: [],
  tournaments: [],
  shopProducts: [],
  rewards: [],
  easterEggs: [],
  boosters: {
    level: [],
    token: [],
    mission: [],
  },
};

/** @type {{identityToken: string, accessToken: string, playerId: string|null, expiresAt: number}|null} */
let sessionCache = null;

const gamanzaHeaders = accessToken => ({
  Accept: "application/json",
  Authorization: `Bearer ${accessToken}`,
});

const parseJsonSafe = async response => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const decodeJwtSub = accessToken => {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(normalized));
    return json?.sub ?? null;
  } catch {
    return null;
  }
};

const decodeJwtExpMs = accessToken => {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return 0;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(normalized));
    return typeof json?.exp === "number" ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

/**
 * @param {string} identityToken
 * @returns {Promise<{accessToken: string, playerId: string|null}>}
 */
export const getGamanzaSession = async identityToken => {
  const {CLIENT_ID, SERVICE_URL} = ConfEnv.GAMANZA;
  if (!CLIENT_ID || !SERVICE_URL || !identityToken) {
    throw new Error("Gamanza config incomplete");
  }

  const now = Date.now();
  if (
    sessionCache &&
    sessionCache.identityToken === identityToken &&
    sessionCache.accessToken &&
    sessionCache.expiresAt > now + 30_000
  ) {
    return {accessToken: sessionCache.accessToken, playerId: sessionCache.playerId};
  }

  const response = await fetch(`${SERVICE_URL}/api/oauth/v1/token`, {
    method: "POST",
    headers: {"Content-Type": "application/json", Accept: "application/json"},
    body: JSON.stringify({
      clientId: CLIENT_ID,
      identityToken,
    }),
  });

  const body = await parseJsonSafe(response);
  if (!response.ok || !body?.accessToken) {
    throw new Error(`Gamanza auth failed (${response.status})`);
  }

  const accessToken = body.accessToken;
  const playerId = decodeJwtSub(accessToken);
  const expiresAt = decodeJwtExpMs(accessToken) || now + 50 * 60 * 1000;

  sessionCache = {identityToken, accessToken, playerId, expiresAt};
  return {accessToken, playerId};
};

export const clearGamanzaSession = () => {
  sessionCache = null;
};

/**
 * Exchange casino identity token for a Gamanza Frontend API access token.
 * @param {string} identityToken
 * @returns {Promise<string>}
 */
export const fetchGamanzaAccessToken = async identityToken => {
  const session = await getGamanzaSession(identityToken);
  return session.accessToken;
};

const fetchGamanzaJson = async (path, accessToken, params) => {
  const url = new URL(`${ConfEnv.GAMANZA.SERVICE_URL}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value == null) continue;
      if (Array.isArray(value)) {
        for (const v of value) {
          url.searchParams.append(key, String(v));
        }
        continue;
      }
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: gamanzaHeaders(accessToken),
  });

  if (!response.ok) {
    return null;
  }

  return parseJsonSafe(response);
};

const asArray = value => (Array.isArray(value) ? value : []);

/** Unwrap Gamanza list envelopes: array, {docs}, {tournaments}, {data:...}. */
const asDocs = value => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.docs)) return value.docs;
  if (Array.isArray(value.tournaments)) return value.tournaments;
  if (Array.isArray(value.items)) return value.items;
  if (value.data != null && value.data !== value) return asDocs(value.data);
  return [];
};

const flattenBundles = bundlesByTimeframe => {
  if (!bundlesByTimeframe || typeof bundlesByTimeframe !== "object") return [];
  const source =
    bundlesByTimeframe.data &&
    typeof bundlesByTimeframe.data === "object" &&
    !Array.isArray(bundlesByTimeframe.data)
      ? bundlesByTimeframe.data
      : bundlesByTimeframe;
  const fromTimeframe = ["daily", "weekly", "montly", "monthly", "lifetime", "range"].flatMap(key =>
    asArray(source[key])
  );
  if (fromTimeframe.length) return fromTimeframe;
  return asDocs(source);
};

const pickEligibilityLabel = (item, locale = "es") => {
  const translations = item?.translations || item?.externalDetails;
  if (Array.isArray(translations) && translations.length) {
    const match =
      translations.find(t => t.language === locale || t.language?.startsWith?.(locale)) || translations[0];
    if (match?.elegibilityDisplayName) return match.elegibilityDisplayName;
  }
  return item?.eligibility?.eligibilityType || "";
};

const pickPrizePool = item => {
  const rewards = asArray(item?.leaderboardRewards).flatMap(lb => asArray(lb.rewards));
  for (const reward of rewards) {
    if (typeof reward.amount === "number") {
      return {amount: reward.amount, currency: reward.currency || reward.type || ""};
    }
  }
  return null;
};

const durationDays = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.max(1, Math.round(ms / 86_400_000));
};

const pickLocalizedName = (item, locale = "es") => {
  if (!item) return "";
  if (typeof item.name === "string" && item.name) return item.name;
  const translations = item.translations || item.externalDetails;
  if (Array.isArray(translations) && translations.length) {
    const match =
      translations.find(t => t.language === locale || t.language?.startsWith?.(locale)) || translations[0];
    return match?.name || match?.title || match?.text || "";
  }
  return item.title || item.label || item._id || "";
};

const pickDisplayName = (item, locale = "es") => {
  if (!item) return "";
  if (typeof item.name === "string" && item.name.trim()) return item.name;
  const translations = item.translations || item.externalDetails;
  if (Array.isArray(translations) && translations.length) {
    const match =
      translations.find(t => t.language === locale || t.language?.startsWith?.(locale)) || translations[0];
    return match?.name || match?.title || match?.text || match?.description || "";
  }
  return item.title || item.label || item.boosterName || "";
};

const TERMS_KEYS = [
  "termsAndConditions",
  "termsAndCondition",
  "termAndConditions",
  "termsAndConditionsText",
  "termsAndConditionsHtml",
  "bundleTermsAndConditions",
  "legalTerms",
  "termsConditions",
  "terms_and_conditions",
  "tnc",
  "terms",
];

const languageMatches = (lang, locale) => {
  if (!lang || !locale) return false;
  const a = String(lang).toLowerCase();
  const b = String(locale).toLowerCase();
  return a === b || a.startsWith(b) || b.startsWith(a);
};

const termsTextFromValue = (value, locale) => {
  if (value == null || value === false) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    if (!value.length) return "";
    const match =
      value.find(entry => languageMatches(entry?.language || entry?.lang || entry?.locale, locale)) ||
      value.find(entry => termsTextFromValue(entry, locale)) ||
      value[0];
    return termsTextFromValue(match, locale);
  }
  if (typeof value === "object") {
    return String(
      value.termsAndConditions ||
        value.termsAndCondition ||
        value.text ||
        value.html ||
        value.content ||
        value.value ||
        ""
    ).trim();
  }
  return "";
};

const pickTermsFromBag = (bag, locale) => {
  if (!bag || typeof bag !== "object") return "";
  for (const key of TERMS_KEYS) {
    if (bag[key] == null) continue;
    const text = termsTextFromValue(bag[key], locale);
    if (text) return text;
  }
  return "";
};

const pickTermsFromLocalized = (list, locale) => {
  if (!Array.isArray(list) || !list.length) return "";
  const match =
    list.find(entry => languageMatches(entry?.language || entry?.lang || entry?.locale, locale)) || list[0];
  return pickTermsFromBag(match, locale) || termsTextFromValue(match, locale);
};

const pickTerms = (item, locale = "es") => {
  if (!item) return "";
  const bags = [
    item,
    item.bundle,
    item.missionBundle,
    item.metadata,
    item.externalDetails,
    item.translations,
    item.missionBundleExternalDetails,
    item.bundleExternalDetails,
  ];

  for (const bag of bags) {
    const direct = pickTermsFromBag(bag, locale);
    if (direct) return direct;
    const localized = pickTermsFromLocalized(bag, locale);
    if (localized) return localized;
  }

  return "";
};

const toProgressPct = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n <= 1) return Math.round(n * 100);
  return Math.max(0, Math.min(100, Math.round(n)));
};

const pickImage = item => {
  if (!item || typeof item !== "object") return null;
  const meta = item.metadata || {};
  const earned = item.earnedReward && item.earnedReward !== item ? item.earnedReward : {};
  return (
    item.smallImage ||
    item.largeImage ||
    item.image ||
    item.imageUrl ||
    item.mobileImage ||
    item.desktopImage ||
    meta.mobileImage ||
    meta.desktopImage ||
    earned.imageUrl ||
    earned.smallImage ||
    earned.largeImage ||
    earned.image ||
    null
  );
};

const mapNestedReward = (item, idx) => {
  const earned = item.earnedReward || {};
  return {
    id: item._id || item.id || `reward-${idx}`,
    name: pickDisplayName(earned) || pickDisplayName(item) || earned.type || item.type || "",
    description: item.description || earned.description || "",
    image: pickImage(item) || pickImage(earned),
    type: String(item.type || item.rewardType || earned.type || "").toLowerCase(),
    locked: item.locked !== false && item.status !== "claimed" && item.status !== "unlocked",
  };
};

const mapObjective = (item, idx) => ({
  id: item._id || item.missionId || item.id || `obj-${idx}`,
  name: pickLocalizedName(item) || pickDisplayName(item),
  progress: toProgressPct(item.state?.progress ?? item.progress),
  image: pickImage(item),
});

const getPath = (obj, path) => path.split(".").reduce((acc, key) => acc?.[key], obj);

const firstArray = (obj, paths) => {
  for (const path of paths) {
    const value = getPath(obj, path);
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
};

const labelFrom = item => {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return (
    pickLocalizedName(item) ||
    pickDisplayName(item) ||
    item.providerName ||
    item.providerId ||
    item.externalId ||
    ""
  );
};

const uniqueLabels = items => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const label = labelFrom(item).trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
};

const PROVIDER_PATHS = [
  "gameProviders",
  "providers",
  "includedProviders",
  "eligibleProviders",
  "casinoFilters.gameProviders",
  "casinoFilters.providers",
  "casinoGameFilter.providers",
  "casinoGameFilter.gameProviders",
  "filters.gameProviders",
  "filters.providers",
  "casino.providers",
  "casino.gameProviders",
  "productFilter.providers",
  "gamesFilter.providers",
  "settings.casino.providers",
  "rules.casino.providers",
];

const CATEGORY_PATHS = [
  "gameCategories",
  "categories",
  "casinoFilters.gameCategories",
  "casinoFilters.categories",
  "filters.gameCategories",
  "filters.categories",
  "casino.categories",
  "casino.gameCategories",
];

const GAME_PATHS = [
  "games",
  "eligibleGames",
  "casinoFilters.games",
  "casinoGameFilter.games",
  "filters.games",
  "casino.games",
  "gamesFilter.games",
];

const pickTournamentGames = item =>
  firstArray(item, GAME_PATHS).map((game, idx) => {
    if (typeof game === "string" || typeof game === "number") {
      return {id: String(game), name: "", provider: "", image: null};
    }
    const provider = game.provider;
    return {
      id: String(game.gameId || game.externalId || game.externalGameId || game._id || game.id || `game-${idx}`),
      name: pickLocalizedName(game) || pickDisplayName(game),
      provider:
        (typeof provider === "string" ? provider : labelFrom(provider)) ||
        game.providerName ||
        game.providerId ||
        "",
      image: pickImage(game),
    };
  });

const pickTournamentProviders = item => {
  const fromList = uniqueLabels(firstArray(item, PROVIDER_PATHS));
  if (fromList.length) return fromList;
  return uniqueLabels(pickTournamentGames(item).map(game => game.provider));
};

const pickTournamentCategories = item => uniqueLabels(firstArray(item, CATEGORY_PATHS));

const toFiniteNumber = value => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.replace(",", "."));
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const PRICE_KEYS = [
  "virtualCurrencyPrice",
  "tokenPrice",
  "tokensPrice",
  "tokenCost",
  "costInTokens",
  "vcPrice",
  "tokens",
];

const readPriceFrom = (value, depth = 0) => {
  if (value == null || depth > 4) return null;
  const direct = toFiniteNumber(value);
  if (direct != null) return direct;
  if (typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = readPriceFrom(entry, depth + 1);
      if (found != null) return found;
    }
    return null;
  }
  for (const key of PRICE_KEYS) {
    if (value[key] == null) continue;
    const found = readPriceFrom(value[key], depth + 1);
    if (found != null) return found;
  }
  for (const key of ["virtualCurrency", "price", "cost"]) {
    if (value[key] == null || value[key] === value) continue;
    const found = readPriceFrom(value[key], depth + 1);
    if (found != null) return found;
  }
  return toFiniteNumber(value.amount) ?? toFiniteNumber(value.value);
};

const pickShopPrice = product => {
  if (!product || typeof product !== "object") return null;
  return (
    readPriceFrom(product.virtualCurrencyPrice) ??
    readPriceFrom(product.tokenPrice) ??
    readPriceFrom(product.tokensPrice) ??
    readPriceFrom(product.tokenCost) ??
    readPriceFrom(product.vcPrice) ??
    readPriceFrom(product.price) ??
    readPriceFrom(product.cost) ??
    readPriceFrom(product.costs) ??
    readPriceFrom(product.pricing) ??
    readPriceFrom(product.virtualCurrency) ??
    readPriceFrom(product.metadata?.virtualCurrencyPrice) ??
    readPriceFrom(product.reward?.virtualCurrencyPrice) ??
    readPriceFrom(product.reward?.price) ??
    readPriceFrom(product.reward?.cost) ??
    null
  );
};

const unwrapShopProduct = value => {
  if (!value || typeof value !== "object") return null;
  if (value._id || value.id || value.price != null || value.virtualCurrencyPrice != null) return value;
  if (value.data && typeof value.data === "object" && !Array.isArray(value.data)) {
    return unwrapShopProduct(value.data);
  }
  return value;
};

const mapShopProduct = p => {
  if (!p || typeof p !== "object") return null;
  const remaining = p.remainingItems ?? p.stock ?? null;
  const maxRaw = Number(p.maxQuantityPerPurchase ?? p.maxQuantity ?? p.limitPerOrder ?? p.maxItems);
  return {
    id: p._id || p.id,
    name: pickLocalizedName(p),
    description:
      (typeof p.description === "string" && p.description) ||
      (Array.isArray(p.translations)
        ? (p.translations.find(tr => tr.language === "es" || tr.language?.startsWith?.("es")) || p.translations[0])
            ?.description
        : "") ||
      "",
    image: pickImage(p),
    status: p.status || "",
    remainingItems: remaining == null || remaining === "" ? null : Number(remaining),
    price: pickShopPrice(p),
    maxQuantity: Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null,
  };
};

const mapBooster = (item, kind, idx) => ({
  id: item._id || item.id || `booster-${kind}-${idx}`,
  name: pickDisplayName(item) || pickDisplayName(item.earnedReward),
  image: pickImage(item),
  kind: "booster",
  boosterKind: kind,
  type: item.type || item.boosterType || "",
  endDate: item.endDate || item.expiresAt || item.validUntil || item.metadata?.endDate || null,
});

const mapReward = item => {
  const earned = item.earnedReward || {};
  return {
    id: item._id || item.id || item.rewardId,
    name: pickDisplayName(earned) || pickDisplayName(item) || earned.type || item.rewardType || item.type || "",
    status: item.status || "",
    rewardType: item.rewardType || earned.type || item.type || "",
    image: pickImage(item) || pickImage(earned),
    groupId: item.group?.groupId || item.groupId || null,
    kind: "reward",
  };
};

const flattenEasterEggs = res => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.categories)) return res.categories;
  if (Array.isArray(res.bundles)) return res.bundles;
  if (Array.isArray(res.easterEggs)) return res.easterEggs;
  if (res.data != null && res.data !== res) return flattenEasterEggs(res.data);
  return asDocs(res);
};

const mapEasterEgg = (egg, idx) => {
  const firstReward = asArray(egg.rewards)[0];
  const condition = egg.condition || {};
  const status = String(condition.status || egg.status || "").toLowerCase();
  return {
    id:
      egg.categoryGroupId ||
      egg.playerCategoryId ||
      egg.easterEggId ||
      condition.easterEggId ||
      egg._id ||
      egg.id ||
      `egg-${idx}`,
    name: pickDisplayName(egg) || pickDisplayName(firstReward),
    image: pickImage(egg) || pickImage(firstReward) || pickImage(condition) || condition.animation || null,
    kind: "easter-egg",
    status,
    easterEggId: egg.easterEggId || condition.easterEggId || null,
    categoryGroupId: egg.categoryGroupId || null,
    playerCategoryId: egg.playerCategoryId || null,
  };
};

/**
 * Player XP (ranks) + virtual currency tokens for UI.
 * @param {string} identityToken
 * @returns {Promise<typeof EMPTY_STATS>}
 */
export const getGamanzaPlayerStats = async identityToken => {
  if (!isGamanzaUiEnabled()) {
    return EMPTY_STATS;
  }

  const {accessToken} = await getGamanzaSession(identityToken);

  const [ranksData, walletData] = await Promise.all([
    fetchGamanzaJson("/ranks/v1/ui/player-card", accessToken),
    fetchGamanzaJson("/virtual-currency/v1/ui/wallet", accessToken),
  ]);

  const xpBalance = Number(ranksData?.xpBalance ?? 0) || 0;
  const tokens = Number(walletData?.balance ?? 0) || 0;
  const level = Number(ranksData?.level?.levelNumber ?? 0) || 0;
  const rankName = ranksData?.rank?.name ?? null;
  const levelProgressRaw = ranksData?.levelProgress ?? ranksData?.level?.progress ?? null;
  const levelProgress =
    levelProgressRaw != null && Number.isFinite(Number(levelProgressRaw))
      ? Math.max(0, Math.min(100, Number(levelProgressRaw)))
      : null;
  const pointsUntilNextLevelRaw = ranksData?.pointsUntilNextLevel ?? null;
  const pointsUntilNextLevel =
    pointsUntilNextLevelRaw != null && Number.isFinite(Number(pointsUntilNextLevelRaw))
      ? Number(pointsUntilNextLevelRaw)
      : null;

  return {
    points: xpBalance,
    xpBalance,
    tokens,
    level,
    rankName,
    levelProgress,
    pointsUntilNextLevel,
  };
};

/**
 * Sidebar-equivalent engagement data for custom Achievements UI.
 * @param {string} identityToken
 * @returns {Promise<typeof EMPTY_ACHIEVEMENTS>}
 */
export const getGamanzaAchievements = async identityToken => {
  if (!isGamanzaUiEnabled()) {
    return EMPTY_ACHIEVEMENTS;
  }

  const {accessToken, playerId} = await getGamanzaSession(identityToken);

  const [
    missionsRes,
    bundlesRes,
    tournamentsRes,
    shopRes,
    rewardsRes,
    easterEggsRes,
    levelBoostersRes,
    tokenBoostersRes,
    missionBoostersRes,
  ] = await Promise.all([
    fetchGamanzaJson("/missions/v2/ui/player-card/mission-list-inprogress", accessToken),
    fetchGamanzaJson("/missions/v2/ui/player-card/bundle-active-timeframe", accessToken),
    fetchGamanzaJson("/api/tournaments/v1/ui/filter-eligible-tournaments-for-player", accessToken),
    fetchGamanzaJson("/reward-shop/v1/ui/items", accessToken, {status: "active", page: 1, limit: 12}),
    fetchGamanzaJson("/reward-processor/v1/ui/rewards", accessToken, {page: 1, limit: 20}),
    fetchGamanzaJson("/missions/v1/ui/player-card/bundle-active-easter-eggs", accessToken),
    fetchGamanzaJson("/ranks/v1/ui/rewards/list-active-boosters", accessToken),
    fetchGamanzaJson("/virtual-currency/v1/ui/token-booster/list-active-boosters", accessToken),
    playerId
      ? fetchGamanzaJson(`/missions/v1/ui/missions-booster/list-active-boosters/${playerId}`, accessToken)
      : Promise.resolve(null),
  ]);

  const missions = asDocs(missionsRes).map(m => ({
    id: m._id || m.missionId || m.id,
    name: pickLocalizedName(m),
    description: m.description || "",
    status: m.status || "",
    progress: toProgressPct(m.state?.progress ?? m.missionBundleState?.progress ?? m.progress),
    completed: m.state?.completed ?? null,
    total: m.state?.total ?? null,
    endDate: m.endDate || m.timePeriod?.endDate || null,
    image: pickImage(m),
    kind: "mission",
    bundleId: m.bundleId || m.missionBundleId || m.bundle?._id || m.bundle?.id || null,
    rewards: asArray(m.rewards).map(mapNestedReward),
    objectives: asArray(m.objectives || m.conditions).map(mapObjective),
    terms: pickTerms(m),
  }));

  const bundles = flattenBundles(bundlesRes).map(b => ({
    id: b._id || b.originalId || b.id,
    name: pickLocalizedName(b),
    description: b.description || "",
    status: b.status || "",
    progress: toProgressPct(b.state?.progress ?? b.progress),
    completed: b.state?.completed ?? null,
    total: b.state?.total ?? null,
    endDate: b.endDate || b.timePeriod?.endDate || null,
    image: pickImage(b),
    kind: "bundle",
    originalId: b.originalId || b.bundleId || null,
    requiresOptIn: Boolean(b.playerRequiresOptIn),
    hasOptedIn: Boolean(b.playerHasOptedIn ?? b.isOptIn),
    rewards: asArray(b.rewards).map(mapNestedReward),
    objectives: asArray(b.missions).map(mapObjective),
    terms: pickTerms(b),
  }));

  bundles.forEach(bundle => {
    if (bundle.objectives.length) return;
    bundle.objectives = missions
      .filter(m => m.bundleId && String(m.bundleId) === String(bundle.id))
      .map((m, idx) => ({
        id: m.id || `obj-${idx}`,
        name: m.name,
        progress: m.progress,
        image: m.image,
      }));
  });

  missions.forEach(mission => {
    if (mission.terms || !mission.bundleId) return;
    const parent = bundles.find(bundle =>
      [bundle.id, bundle.originalId].filter(Boolean).some(id => String(id) === String(mission.bundleId))
    );
    if (parent?.terms) mission.terms = parent.terms;
  });

  const tournaments = asDocs(tournamentsRes).map(t => {
    const startDate = t.timePeriod?.startDate || null;
    const endDate = t.timePeriod?.endDate || null;
    return {
      id: t._id || t.id,
      name: pickLocalizedName(t),
      status: t.status || "",
      optIn: Boolean(t.optIn),
      isPlayerOptIn: Boolean(t.isPlayerOptIn),
      image: t.largeImage || t.smallImage || null,
      type: t.type || "",
      startDate,
      endDate,
      eligibilityType: t.eligibility?.eligibilityType || "",
      eligibilityLabel: pickEligibilityLabel(t),
      prize: pickPrizePool(t),
      durationDays: durationDays(startDate, endDate),
      providers: pickTournamentProviders(t),
      categories: pickTournamentCategories(t),
      games: pickTournamentGames(t),
    };
  });

  const shopProducts = asDocs(shopRes).map(mapShopProduct).filter(Boolean);

  const rewards = asDocs(rewardsRes).map(mapReward);
  const easterEggs = flattenEasterEggs(easterEggsRes).map(mapEasterEgg);

  return {
    missions,
    bundles,
    tournaments,
    shopProducts,
    rewards,
    easterEggs,
    boosters: {
      level: asDocs(levelBoostersRes).map((item, idx) => mapBooster(item, "level", idx)),
      token: asDocs(tokenBoostersRes).map((item, idx) => mapBooster(item, "token", idx)),
      mission: asDocs(missionBoostersRes).map((item, idx) => mapBooster(item, "mission", idx)),
    },
  };
};

/**
 * Claim an active easter egg for the current player.
 * @param {string} identityToken
 * @param {{easterEggId?: string, categoryGroupId?: string, playerCategoryId?: string}} payload
 * @returns {Promise<boolean>}
 */
export const claimGamanzaEasterEgg = async (identityToken, payload) => {
  if (!isGamanzaUiEnabled()) return false;
  const {accessToken} = await getGamanzaSession(identityToken);
  const body = {};
  if (payload?.easterEggId) body.easterEggId = payload.easterEggId;
  if (payload?.categoryGroupId) body.categoryGroupId = payload.categoryGroupId;
  if (payload?.playerCategoryId) body.playerCategoryId = payload.playerCategoryId;

  const response = await fetch(`${ConfEnv.GAMANZA.SERVICE_URL}/missions/v1/ui/easter-egg/claim`, {
    method: "POST",
    headers: {...gamanzaHeaders(accessToken), "Content-Type": "application/json"},
    body: JSON.stringify(body),
  });

  return response.ok || response.status === 201;
};

/**
 * Place a Reward Shop order for the current player.
 * @param {string} identityToken
 * @param {{itemId: string, quantity: number}} payload
 * @returns {Promise<boolean>}
 */
export const purchaseGamanzaShopItem = async (identityToken, payload) => {
  if (!isGamanzaUiEnabled()) return false;
  const {accessToken, playerId} = await getGamanzaSession(identityToken);
  const quantity = Math.max(1, Number(payload?.quantity) || 1);
  const body = {itemId: payload?.itemId, quantity};
  if (playerId) body.playerId = playerId;

  const response = await fetch(`${ConfEnv.GAMANZA.SERVICE_URL}/reward-shop/v1/ui/orders`, {
    method: "POST",
    headers: {...gamanzaHeaders(accessToken), "Content-Type": "application/json"},
    body: JSON.stringify(body),
  });

  return response.ok || response.status === 201;
};

/**
 * Full Reward Shop product, including token price.
 * @param {string} identityToken
 * @param {string} itemId
 * @returns {Promise<object|null>}
 */
export const getGamanzaShopItem = async (identityToken, itemId) => {
  if (!isGamanzaUiEnabled() || !itemId) return null;
  const {accessToken} = await getGamanzaSession(identityToken);
  const raw = await fetchGamanzaJson(`/reward-shop/v1/ui/items/${itemId}`, accessToken);
  return mapShopProduct(unwrapShopProduct(raw));
};
